import * as SwitchPrimitive from "@radix-ui/react-switch";
import * as React from "react";
import { cn } from "@/lib/utils";

// ─────────────────────────────────────────────
// Switch (toggle)
//
// 44x24px toggle. Design: §6.6 Selection Controls
// ─────────────────────────────────────────────

const Switch = React.forwardRef<
  React.ElementRef<typeof SwitchPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof SwitchPrimitive.Root>
>(({ className, ...props }, ref) => (
  <SwitchPrimitive.Root
    ref={ref}
    className={cn(
      "peer inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent",
      "transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-1 focus-visible:ring-offset-page",
      "disabled:cursor-not-allowed disabled:opacity-50",
      "bg-hover data-[state=checked]:bg-accent",
      className
    )}
    {...props}
  >
    <SwitchPrimitive.Thumb
      className={cn(
        "pointer-events-none block h-5 w-5 rounded-full bg-white shadow-sm ring-0",
        "transition-transform data-[state=checked]:translate-x-5 data-[state=unchecked]:translate-x-0"
      )}
    />
  </SwitchPrimitive.Root>
));
Switch.displayName = SwitchPrimitive.Root.displayName;

// ── SwitchWithLabel ──────────────────────────
interface SwitchWithLabelProps
  extends React.ComponentPropsWithoutRef<typeof SwitchPrimitive.Root> {
  label: string;
  description?: string;
}

function SwitchWithLabel({ label, description, id, className, ...props }: SwitchWithLabelProps) {
  const switchId = id ?? label.toLowerCase().replace(/\s+/g, "-");
  return (
    <div className={cn("flex items-center justify-between gap-4", className)}>
      <div className="flex flex-col gap-0.5">
        <label
          htmlFor={switchId}
          className="cursor-pointer text-[14px] font-medium text-text-secondary"
        >
          {label}
        </label>
        {description && (
          <p className="text-caption text-text-muted">{description}</p>
        )}
      </div>
      <Switch id={switchId} {...props} />
    </div>
  );
}

export { Switch, SwitchWithLabel };
